<template>
  <aside class="parking-stops-sidebar" aria-label="Настройки парковочных мест">
    <div class="parking-stops-sidebar__tabs" role="tablist" aria-label="Разделы настроек камеры">
      <button
        id="parking-stops-tab"
        class="parking-stops-sidebar__tab"
        :class="{'is-active': activeTab === 'parking'}"
        type="button"
        role="tab"
        :aria-selected="String(activeTab === 'parking')"
        aria-controls="parking-stops-panel"
        @click="activeTab = 'parking'"
      >
        Парковочные зоны
      </button>
      <button
        v-if="showSchedule"
        id="parking-schedule-tab"
        class="parking-stops-sidebar__tab"
        :class="{'is-active': activeTab === 'schedule'}"
        type="button"
        role="tab"
        :aria-selected="String(activeTab === 'schedule')"
        aria-controls="parking-schedule-panel"
        @click="activeTab = 'schedule'"
      >
        Расписание
      </button>
    </div>

    <section
      v-if="activeTab === 'parking'"
      id="parking-stops-panel"
      class="parking-stops-sidebar__panel parking-stops-sidebar__panel_parking"
      role="tabpanel"
      aria-labelledby="parking-stops-tab"
    >
      <div class="parking-stops-sidebar__card">
        <h3 class="parking-stops-sidebar__title">
          <span
            class="parking-stops-sidebar__color"
            :style="{backgroundColor: parkingColor}"
            aria-hidden="true"
          />
          <span>{{ displayParkingName }}</span>
        </h3>

        <div class="parking-stops-sidebar__count">
          <span>Парковочные места</span>
          <strong>{{ normalizedSpotsCount }}</strong>
        </div>

        <button
          class="parking-stops-sidebar__add"
          type="button"
          aria-label="Добавить парковочное место"
          @click="$emit('add')"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d="M12 5v14M5 12h14" />
          </svg>
          <span>Добавить место</span>
        </button>
      </div>

      <div class="parking-stops-sidebar__actions">
        <button
          class="parking-stops-sidebar__button parking-stops-sidebar__button_outline"
          type="button"
          @click="$emit('clear')"
        >
          Очистить
        </button>
        <button
          class="parking-stops-sidebar__button parking-stops-sidebar__button_primary"
          type="button"
          @click="$emit('save')"
        >
          Сохранить
        </button>
      </div>
    </section>

    <section
      v-else
      id="parking-schedule-panel"
      class="parking-stops-sidebar__panel parking-stops-sidebar__panel_schedule"
      role="tabpanel"
      aria-labelledby="parking-schedule-tab"
    >
      <ScheduleList
        :camera-number="cameraNumber"
        :analytic-type="analyticType"
      />
    </section>
  </aside>
</template>

<script>
import ScheduleList from "@/components/pacs/analytics/schedule/ScheduleList.vue";

/**
 * Боковая панель управления парковочными местами камеры.
 */
export default {
  name: "ParkingStopsSidebar",
  components: {
    ScheduleList,
  },
  props: {
    parkingName: {
      type: String,
      default: "",
    },
    parkingColor: {
      type: String,
      default: "#20b998",
    },
    spotsCount: {
      type: Number,
      default: 0,
    },
    showSchedule: {
      type: Boolean,
      default: false,
    },
    cameraNumber: {
      type: String,
      default: "",
    },
    analyticType: {
      type: String,
      default: "perimeter_security",
    },
  },
  data() {
    return {
      activeTab: "parking",
    };
  },
  computed: {
    displayParkingName() {
      return this.parkingName.trim() || "Парковка";
    },
    normalizedSpotsCount() {
      return Math.max(0, Math.trunc(this.spotsCount));
    },
  },
};
</script>

<style lang="scss" scoped>
.parking-stops-sidebar {
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100%;
  color: #202124;
  background: #fff;

  &__tabs {
    display: flex;
    min-height: 44px;
    padding: 0 16px;
    border-bottom: 1px solid #e5e7eb;
    background: #fafafa;
  }

  &__tab {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 0 8px;
    border: 0;
    color: #272727;
    background: transparent;
    font: inherit;
    font-size: 16px;
    white-space: nowrap;
    cursor: pointer;

    &::after {
      position: absolute;
      right: 8px;
      bottom: -1px;
      left: 8px;
      height: 2px;
      background: transparent;
      content: "";
    }

    &.is-active {
      font-weight: 500;

      &::after {
        background: #242424;
      }
    }

    &:focus-visible {
      border-radius: 4px;
      outline: 2px solid #3183f5;
      outline-offset: -2px;
    }
  }

  &__panel {
    min-height: 0;
    padding: 16px;

    &_parking {
      display: flex;
      flex: 1;
      flex-direction: column;
    }

    &_schedule {
      overflow-y: auto;
    }
  }

  &__card {
    padding: 18px 16px 16px;
    border-radius: 12px;
    background: #fafafa;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    line-height: 1.35;
  }

  &__color {
    flex: 0 0 20px;
    width: 20px;
    height: 20px;
    border-radius: 5px;
  }

  &__count {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-top: 16px;
    font-size: 15px;

    strong {
      font-size: 17px;
      font-weight: 600;
    }
  }

  &__add {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    margin-top: 24px;
    padding: 0;
    border: 0;
    color: #3183f5;
    background: transparent;
    font: inherit;
    font-weight: 500;
    cursor: pointer;

    svg {
      width: 24px;
      height: 24px;
      fill: none;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-width: 1.5;
    }

    &:hover {
      color: #166fd8;
    }

    &:focus-visible {
      border-radius: 4px;
      outline: 2px solid currentColor;
      outline-offset: 4px;
    }
  }

  &__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-top: 16px;
  }

  &__button {
    min-width: 0;
    height: 40px;
    padding: 0 12px;
    border: 1px solid #3183f5;
    border-radius: 22px;
    font: inherit;
    font-weight: 500;
    cursor: pointer;

    &_outline {
      color: #3183f5;
      background: #fff;
    }

    &_primary {
      color: #fff;
      background: #3183f5;
    }

    &:hover {
      box-shadow: 0 4px 12px rgb(49 131 245 / 18%);
    }

    &:focus-visible {
      outline: 2px solid #175fae;
      outline-offset: 2px;
    }
  }
}
</style>
